/**
 * Cohort aggregation for anonymous (lead-magnet) capacity responses.
 *
 * Why this is separate from the MAPS rollups in `mapsPatientFromAssessments` /
 * `assessmentRosterLink`: those are keyed to a roster employee (by
 * `employee_db_id`, falling back to an email match) and return nothing when the
 * roster is empty. Anonymous responses have neither, by design — so they are
 * invisible to every one of those helpers. Rather than loosen roster scoping,
 * which exists to keep one client's employees apart, anonymous data gets its own
 * purely cohort-level path. It is never about an individual.
 */
import type { AssessmentSessionRow } from "@/lib/api/assessments";
import { MAPS_REGION_ROWS, type MapsRegionRow } from "@/lib/mapsRegionalDisplay";

/**
 * Minimum responses before anything is reported — overall AND per segment.
 * Protects individuals (a group this size cannot be narrowed to one person) and
 * stops a "report" built from a handful of answers. Spec §3.3/§4.
 */
export const LEAD_MAGNET_MIN_RESPONSES = 10;

export type RegionAggregate = {
  label: string;
  /** Mean capacity %, 0–100. Higher is better. Null when nobody answered. */
  avgCapacity: number | null;
  /** Mean pain, 0–10. Higher is worse. Null when nobody answered. */
  avgPain: number | null;
  /** Capacity lost, i.e. 100 - avgCapacity. Null when avgCapacity is null. */
  capacityLoss: number | null;
  /** How many responses contributed a capacity value for this region. */
  responses: number;
};

export type SegmentAggregate = {
  id: string;
  name: string;
  responses: number;
  /**
   * True when this segment has fewer than the floor, so it is NOT reported on
   * its own. Its responses still count toward the overall totals — they are
   * folded in, never discarded.
   */
  suppressed: boolean;
  /** Null when suppressed. */
  regions: RegionAggregate[] | null;
};

export type LeadMagnetReport = {
  /** Every anonymous response received, including ones excluded below. */
  totalReceived: number;
  /** Excluded as straight-lined. Reported so the denominator stays honest. */
  excludedLowQuality: number;
  /** Responses actually counted. */
  counted: number;
  /** False until `counted` reaches the floor — show nothing at all before then. */
  ready: boolean;
  /** All six regions, in the standard display order. */
  regions: RegionAggregate[];
  /** Same data, worst first. The "biggest problem areas". */
  rankedRegions: RegionAggregate[];
  locations: SegmentAggregate[];
  jobCategories: SegmentAggregate[];
  /** How many segments were folded into the overall for being under the floor. */
  suppressedSegmentCount: number;
};

export type SegmentOption = { id: string; name: string };

function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return Math.round((sum / values.length) * 10) / 10;
}

/**
 * Region values for a cohort.
 *
 * Note this treats 0 as a real value, unlike `readRegionCapacity`, which maps 0
 * to "missing" for the single-employee MAPS panels. Every submission writes a
 * computed score for all six regions, so a stored 0 here means genuinely zero
 * capacity, not an unanswered section — and dropping those would flatter the
 * result by hiding the worst cases.
 */
function aggregateRegion(
  sessions: AssessmentSessionRow[],
  row: MapsRegionRow,
): RegionAggregate {
  const caps: number[] = [];
  const pains: number[] = [];
  for (const s of sessions) {
    const cap = s[row.capKey];
    if (typeof cap === "number" && Number.isFinite(cap)) caps.push(cap);
    const pain = s[row.painKey];
    if (typeof pain === "number" && Number.isFinite(pain)) pains.push(pain);
  }
  const avgCapacity = mean(caps);
  return {
    label: row.label,
    avgCapacity,
    avgPain: mean(pains),
    capacityLoss: avgCapacity == null ? null : Math.round((100 - avgCapacity) * 10) / 10,
    responses: caps.length,
  };
}

function aggregateAllRegions(sessions: AssessmentSessionRow[]): RegionAggregate[] {
  return MAPS_REGION_ROWS.map((row) => aggregateRegion(sessions, row));
}

/**
 * Worst first: most capacity lost, then most pain. Regions nobody answered sort
 * last rather than reading as "no problem here".
 */
function rankRegions(regions: RegionAggregate[]): RegionAggregate[] {
  return [...regions].sort((a, b) => {
    if (a.capacityLoss == null && b.capacityLoss == null) return 0;
    if (a.capacityLoss == null) return 1;
    if (b.capacityLoss == null) return -1;
    if (b.capacityLoss !== a.capacityLoss) return b.capacityLoss - a.capacityLoss;
    return (b.avgPain ?? 0) - (a.avgPain ?? 0);
  });
}

/** Anonymous responses that count: lead-magnet source, not flagged as junk. */
export function countableLeadMagnetSessions(
  sessions: AssessmentSessionRow[],
): AssessmentSessionRow[] {
  return sessions.filter(
    (s) => s.session_source === "lead_magnet" && !s.quality_flag,
  );
}

function aggregateSegments(
  sessions: AssessmentSessionRow[],
  options: SegmentOption[],
  key: "location_snapshot_id" | "job_category_snapshot_id",
  minResponses: number,
): SegmentAggregate[] {
  const byId = new Map<string, AssessmentSessionRow[]>();
  for (const s of sessions) {
    const id = s[key];
    if (typeof id !== "string" || id.trim() === "") continue;
    const arr = byId.get(id) ?? [];
    arr.push(s);
    byId.set(id, arr);
  }

  return options
    .map((opt) => {
      const rows = byId.get(opt.id) ?? [];
      const suppressed = rows.length < minResponses;
      return {
        id: opt.id,
        name: opt.name,
        responses: rows.length,
        suppressed,
        regions: suppressed ? null : aggregateAllRegions(rows),
      };
    })
    .sort((a, b) => b.responses - a.responses || a.name.localeCompare(b.name));
}

/**
 * Build the anonymous cohort report.
 *
 * `locations` / `jobCategories` are the organization's configured options, used
 * so a segment with zero responses still appears (as suppressed) rather than
 * silently vanishing.
 */
export function buildLeadMagnetReport(args: {
  sessions: AssessmentSessionRow[];
  locations?: SegmentOption[];
  jobCategories?: SegmentOption[];
  minResponses?: number;
  /**
   * Floor for showing a location or job-category breakdown, when it should
   * differ from the floor for showing the report at all.
   *
   * Exists for the live presentation view, where the room chart builds from
   * the first response but a segment of three people is still too small to
   * show on its own. Defaults to minResponses, so every existing caller keeps
   * one number governing both.
   */
  minSegmentResponses?: number;
}): LeadMagnetReport {
  const minResponses = args.minResponses ?? LEAD_MAGNET_MIN_RESPONSES;
  const minSegmentResponses = args.minSegmentResponses ?? minResponses;
  const all = args.sessions.filter((s) => s.session_source === "lead_magnet");
  const counted = countableLeadMagnetSessions(args.sessions);

  const regions = aggregateAllRegions(counted);
  const locations = aggregateSegments(
    counted,
    args.locations ?? [],
    "location_snapshot_id",
    minSegmentResponses,
  );
  const jobCategories = aggregateSegments(
    counted,
    args.jobCategories ?? [],
    "job_category_snapshot_id",
    minSegmentResponses,
  );

  return {
    totalReceived: all.length,
    excludedLowQuality: all.length - counted.length,
    counted: counted.length,
    ready: counted.length >= minResponses,
    regions,
    rankedRegions: rankRegions(regions),
    locations,
    jobCategories,
    suppressedSegmentCount:
      locations.filter((s) => s.suppressed).length +
      jobCategories.filter((s) => s.suppressed).length,
  };
}

/**
 * One-line summary of what was counted, for the report header. Being explicit
 * about the denominator is the difference between a credible report and one that
 * looks like it is hiding something.
 */
export function leadMagnetCountSummary(report: LeadMagnetReport): string {
  if (report.excludedLowQuality === 0) {
    return `${report.counted} ${report.counted === 1 ? "response" : "responses"}`;
  }
  return `${report.counted} of ${report.totalReceived} responses counted (${report.excludedLowQuality} incomplete or not answered in good faith)`;
}
